import { darkComponents, iconSizes, lightComponents } from '@mds/tokens';
import { avatarClasses, chipClasses, type Components } from '@mui/material';
import { CSSProperties } from 'react';
import { clearIconSizeReduction } from '../../consts/theme';
import { themeVars } from '../themeVars';
import { paletteColorNames, typographyStyles, type Theme } from './utils';

const chipTokens = lightComponents.chip;
const darkChipTokens = darkComponents.chip;
const chipSizeTokens = chipTokens.size;
type ChipTokens = typeof chipTokens | typeof darkChipTokens;
const chipColorTokenNames = ['primary', 'error', 'warning', 'info', 'success'] as const;
type ChipColorTokenName = (typeof chipColorTokenNames)[number];

const isOutlineVariant = (variant: unknown): boolean => variant === 'outline' || variant === 'outlined';
const isFilledVariant = (variant: unknown): boolean => variant === 'filled' || variant === 'solid';
const isClickable = (clickable: unknown): boolean => clickable === true;
const isStaticChip = (clickable: unknown): boolean => clickable !== true;
const focusVisibleSelector = `&:focus-visible, &.${chipClasses.focusVisible}`;

const hasChipColorTokens = (color: string): color is ChipColorTokenName =>
  chipColorTokenNames.includes(color as ChipColorTokenName);

const overlay = (color: string) => `linear-gradient(${color}, ${color})`;

const deleteIconMargin = (iconSize: number) => `0 ${iconSize - 4}px 0 -${iconSize - 10}px`;
const deleteIconSize = (iconSize: number) => iconSize - clearIconSizeReduction;
const baseIconMargin = (iconSize: number) => ({
  marginLeft: iconSize / 2,
  marginRight: -(iconSize / 8),
});

const avatarStyles = (avatarHeight: number, left: number, fontSize: string): CSSProperties => ({
  position: 'relative',
  left,
  width: avatarHeight,
  height: avatarHeight,
  fontSize,
  marginLeft: 0,
  marginRight: 0,
});

const chipIconStyles = (color: string): CSSProperties => ({
  color,
});

const commonChipColorVariables = (tokens: ChipTokens) => ({
  '--mds-Chip-avatarBg': tokens.filled.info.background['@'],
  '--mds-Chip-avatarColor': tokens.filled.info.label['@'],
  '--mds-Chip-deleteIconHoverColor': tokens.deleteIcon.colour.hover,
  '--mds-Chip-disabledBg': tokens.background.disabled,
  '--mds-Chip-disabledBorderColor': tokens.border['@'],
  '--mds-Chip-disabledColor': tokens.label.disabled,
  '--mds-Chip-focusBorderColor': tokens.border.focus,
});

const focusRingStyles = () => ({
  position: 'relative',
  boxShadow: 'none',
  '&::after': {
    border: '1px solid',
    borderColor: 'var(--mds-Chip-focusBorderColor)',
    borderRadius: chipTokens.radius,
    bottom: -2,
    content: '""',
    left: -2,
    pointerEvents: 'none',
    position: 'absolute',
    right: -2,
    top: -2,
  },
});

const disabledChipStyles = (options: { outlined?: boolean } = {}) => ({
  opacity: 1,
  color: 'var(--mds-Chip-disabledColor)',
  backgroundColor: 'var(--mds-Chip-disabledBg)',
  ...(options.outlined
    ? {
        borderWidth: chipTokens.borderWidth,
        borderColor: 'var(--mds-Chip-disabledBorderColor)',
      }
    : {}),
  [`& .${chipClasses.deleteIcon}, & .${chipClasses.avatar}, & .${chipClasses.icon}`]: {
    color: 'var(--mds-Chip-disabledColor)',
  },
  [`& .${chipClasses.avatar}.${avatarClasses.root}`]: {
    color: 'var(--mds-Chip-disabledColor)',
    backgroundColor: 'var(--mds-Chip-disabledBg)',
  },
  [`&:hover .${chipClasses.deleteIcon}`]: {
    color: 'var(--mds-Chip-disabledColor)',
  },
});

const selectedChipStyles = (variant: 'soft' | 'filled' | 'outlined') => {
  const variablePrefix = `--mds-Chip-${variant}Selected`;
  const selectedStateSelector = `&:not(.${chipClasses.disabled}), &:not(.${chipClasses.disabled}):focus-visible, &.${chipClasses.focusVisible}:not(.${chipClasses.disabled})`;
  const selectedHoverSelector = `&.${chipClasses.clickable}:not(.${chipClasses.disabled}):hover`;

  return {
    [selectedStateSelector]: {
      color: `var(${variablePrefix}Color)`,
      backgroundColor: `var(${variablePrefix}Bg)`,
      backgroundImage: 'none',
      ...(variant === 'outlined' ? { borderColor: `var(${variablePrefix}BorderColor)` } : {}),
      [`& .${chipClasses.icon}, & .${chipClasses.deleteIcon}`]: chipIconStyles(`var(${variablePrefix}Color)`),
    },
    [selectedHoverSelector]: {
      backgroundImage: `var(${variablePrefix}HoverOverlay)`,
    },
  };
};

export const MuiChip: Components<Theme>['MuiChip'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      ...commonChipColorVariables(chipTokens),
      ...theme.applyStyles('dark', commonChipColorVariables(darkChipTokens)),
      borderRadius: chipTokens.radius,
      [`& .${chipClasses.avatar}`]: {
        color: 'var(--mds-Chip-avatarColor)',
        backgroundColor: 'var(--mds-Chip-avatarBg)',
      },
      [`& .${chipClasses.label} svg + *`]: {
        lineHeight: 1,
      },
      // TODO There should be a separate hover on the icon itself
      [`&:hover .${chipClasses.deleteIcon}`]: {
        color: 'var(--mds-Chip-deleteIconHoverColor)',
      },
      '&:active': {
        boxShadow: 'none',
      },
    }),
    label: {
      paddingLeft: chipSizeTokens.medium.paddingX,
      paddingRight: chipSizeTokens.medium.paddingX,
    },
    avatar: {
      // Assumes a contrasty avatar
      color: 'white',
    },
    icon: {
      ...baseIconMargin(iconSizes.md),
      color: 'inherit',
    },
    deleteIcon: ({ theme }) => ({
      fontSize: deleteIconSize(iconSizes.md),
      margin: deleteIconMargin(deleteIconSize(iconSizes.md)),
      color: themeVars(theme).palette.text.secondary,
      transition: theme.transitions.create('color', { duration: theme.transitions.duration.short }),
    }),
  },
  variants: [
    {
      props: (props) => !props.color || props.color === 'default',
      style: ({ theme }) => {
        const variablesFor = (tokens: ChipTokens) => {
          const softHoverOverlay = overlay(tokens.soft.background.hover);
          const filledHoverOverlay = overlay(tokens.filled.background.hover);

          return {
            '--mds-Chip-softColor': tokens.soft.label,
            '--mds-Chip-softBg': tokens.soft.background['@'],
            '--mds-Chip-hoverOverlay': tokens.background.hover,
            '--mds-Chip-softHoverOverlay': softHoverOverlay,
            '--mds-Chip-softIconColor': tokens.icon.colour,
            '--mds-Chip-softSelectedColor': tokens.selected.label,
            '--mds-Chip-softSelectedBg': tokens.soft.background.selected,
            '--mds-Chip-softSelectedHoverOverlay': softHoverOverlay,
            '--mds-Chip-filledColor': tokens.filled.label,
            '--mds-Chip-filledBg': tokens.filled.background['@'],
            '--mds-Chip-filledHoverOverlay': filledHoverOverlay,
            '--mds-Chip-filledSelectedColor': tokens.filled.label,
            '--mds-Chip-filledSelectedBg': tokens.filled.background.selected,
            '--mds-Chip-filledSelectedHoverOverlay': filledHoverOverlay,
            '--mds-Chip-outlinedColor': tokens.outlined.label,
            '--mds-Chip-outlinedBg': tokens.background['@'],
            '--mds-Chip-outlinedHoverBg': tokens.outlined.background.hover,
            '--mds-Chip-outlinedBorderColor': tokens.border['@'],
            '--mds-Chip-outlinedIconColor': tokens.outlined.label,
            '--mds-Chip-outlinedSelectedColor': tokens.selected.label,
            '--mds-Chip-outlinedSelectedBg': tokens.outlined.background.selected,
            '--mds-Chip-outlinedSelectedHoverOverlay': overlay(tokens.outlined.background.hover),
            '--mds-Chip-outlinedSelectedBorderColor': tokens.outlined.background.selected,
          };
        };

        return {
          ...variablesFor(chipTokens),
          ...theme.applyStyles('dark', variablesFor(darkChipTokens)),
        };
      },
    },
    ...paletteColorNames.map((color) => ({
      props: { color },
      style: ({ theme }: { theme: Theme }) => {
        const palette = themeVars(theme).palette;
        const paletteColor = palette[color];
        const variablesFor = (tokens: ChipTokens) => {
          const hoverOverlay = overlay(tokens.background.hover);
          const colorTokens = hasChipColorTokens(color)
            ? {
                filled: tokens.filled[color],
                outlined: tokens.outlined[color],
                soft: tokens.soft[color],
              }
            : undefined;

          return {
            '--mds-Chip-softColor': colorTokens?.soft.label['@'] ?? paletteColor.softText,
            '--mds-Chip-softBg': colorTokens?.soft.background['@'] ?? paletteColor.softBg,
            '--mds-Chip-hoverOverlay': tokens.background.hover,
            '--mds-Chip-softHoverOverlay': colorTokens ? overlay(colorTokens.soft.background.hover) : hoverOverlay,
            '--mds-Chip-softIconColor': colorTokens?.soft.label['@'] ?? paletteColor.softText,
            '--mds-Chip-softSelectedColor': colorTokens?.soft.label.selected ?? paletteColor.solidColor,
            '--mds-Chip-softSelectedBg': colorTokens?.soft.background.selected ?? paletteColor.solidBg,
            '--mds-Chip-softSelectedHoverOverlay': colorTokens
              ? overlay(colorTokens.soft.background.hover)
              : hoverOverlay,
            '--mds-Chip-filledColor': colorTokens?.filled.label['@'] ?? paletteColor.solidColor,
            '--mds-Chip-filledBg': colorTokens?.filled.background['@'] ?? paletteColor.solidBg,
            '--mds-Chip-filledHoverOverlay': colorTokens ? overlay(colorTokens.filled.background.hover) : hoverOverlay,
            '--mds-Chip-filledSelectedColor': colorTokens?.filled.label.selected ?? paletteColor.solidColor,
            '--mds-Chip-filledSelectedBg': colorTokens?.filled.background.selected ?? paletteColor.solidBg,
            '--mds-Chip-filledSelectedHoverOverlay': colorTokens
              ? overlay(colorTokens.filled.background.hover)
              : hoverOverlay,
            '--mds-Chip-outlinedColor': colorTokens?.outlined.label['@'] ?? palette.text.primary,
            '--mds-Chip-outlinedBg': colorTokens?.outlined.background['@'] ?? paletteColor.subtle,
            '--mds-Chip-outlinedHoverBg': colorTokens?.outlined.background.hover ?? tokens.background.hover,
            '--mds-Chip-outlinedBorderColor': colorTokens?.outlined.border['@'] ?? paletteColor.border,
            '--mds-Chip-outlinedIconColor': colorTokens?.outlined.label['@'] ?? palette.text.secondary,
            '--mds-Chip-outlinedSelectedColor': colorTokens?.outlined.label.selected ?? paletteColor.solidColor,
            '--mds-Chip-outlinedSelectedBg': colorTokens?.outlined.background.selected ?? paletteColor.solidBg,
            '--mds-Chip-outlinedSelectedHoverOverlay': colorTokens
              ? overlay(colorTokens.outlined.background.hover)
              : hoverOverlay,
            '--mds-Chip-outlinedSelectedBorderColor': colorTokens?.outlined.border.selected ?? paletteColor.solidBg,
          };
        };

        return {
          ...variablesFor(chipTokens),
          ...theme.applyStyles('dark', variablesFor(darkChipTokens)),
        };
      },
    })),
    {
      props: (props) => props.variant === 'soft' && isStaticChip(props.clickable),
      style: {
        color: 'var(--mds-Chip-softColor)',
        backgroundColor: 'var(--mds-Chip-softBg)',
        border: 0,
        [`& .${chipClasses.icon}, & .${chipClasses.deleteIcon}`]: chipIconStyles('var(--mds-Chip-softIconColor)'),
        [`&:hover .${chipClasses.deleteIcon}`]: chipIconStyles('var(--mds-Chip-softIconColor)'),
        [focusVisibleSelector]: {
          color: 'var(--mds-Chip-softColor)',
          backgroundColor: 'var(--mds-Chip-softBg)',
          backgroundImage: 'none',
          ...focusRingStyles(),
        },
        [`&.${chipClasses.disabled}`]: disabledChipStyles(),
      },
    },
    {
      props: { variant: 'soft', clickable: true },
      style: {
        color: 'var(--mds-Chip-softColor)',
        backgroundColor: 'var(--mds-Chip-softBg)',
        border: 0,
        '&:hover': {
          color: 'var(--mds-Chip-softColor)',
          backgroundColor: 'var(--mds-Chip-softBg)',
          backgroundImage: 'var(--mds-Chip-softHoverOverlay)',
        },
        [focusVisibleSelector]: {
          color: 'var(--mds-Chip-softColor)',
          backgroundColor: 'var(--mds-Chip-softBg)',
          backgroundImage: 'none',
          ...focusRingStyles(),
        },
        [`& .${chipClasses.deleteIcon}`]: {
          color: 'var(--mds-Chip-softIconColor)',
        },
        [`&:hover .${chipClasses.deleteIcon}`]: {
          color: 'var(--mds-Chip-softIconColor)',
        },
        [`&.${chipClasses.disabled}`]: disabledChipStyles(),
        [`& .${chipClasses.icon}`]: {
          color: 'var(--mds-Chip-softIconColor)',
        },
      },
    },
    {
      props: (props) => isFilledVariant(props.variant) && isStaticChip(props.clickable),
      style: {
        color: 'var(--mds-Chip-filledColor)',
        backgroundColor: 'var(--mds-Chip-filledBg)',
        border: 0,
        [`& .${chipClasses.icon}, & .${chipClasses.deleteIcon}`]: chipIconStyles('inherit'),
        [`&:hover .${chipClasses.deleteIcon}`]: chipIconStyles('inherit'),
        [focusVisibleSelector]: {
          color: 'var(--mds-Chip-filledColor)',
          backgroundColor: 'var(--mds-Chip-filledBg)',
          ...focusRingStyles(),
        },
        [`&.${chipClasses.disabled}`]: disabledChipStyles(),
      },
    },
    {
      props: (props) => isFilledVariant(props.variant) && isClickable(props.clickable),
      style: {
        '&': {
          color: 'var(--mds-Chip-filledColor)',
          backgroundColor: 'var(--mds-Chip-filledBg)',
          border: 0,
        },
        '&:hover': {
          color: 'var(--mds-Chip-filledColor)',
          backgroundColor: 'var(--mds-Chip-filledBg)',
          backgroundImage: 'var(--mds-Chip-filledHoverOverlay)',
        },
        '&, &:hover': {
          [`& .${chipClasses.deleteIcon}`]: {
            color: 'inherit',
            backgroundColor: 'transparent',
          },
        },
        [focusVisibleSelector]: {
          color: 'var(--mds-Chip-filledColor)',
          backgroundColor: 'var(--mds-Chip-filledBg)',
          ...focusRingStyles(),
        },
        [`& .${chipClasses.avatar}`]: {
          // Assumes a contrasty avatar (blue by default in MDS)
          color: 'white',
        },
        [`&.${chipClasses.disabled}`]: disabledChipStyles(),
      },
    },
    {
      props: (props) => isOutlineVariant(props.variant) && isStaticChip(props.clickable),
      style: {
        color: 'var(--mds-Chip-outlinedColor)',
        backgroundColor: 'var(--mds-Chip-outlinedBg)',
        borderColor: 'var(--mds-Chip-outlinedBorderColor)',
        borderWidth: chipTokens.borderWidth,
        borderStyle: 'solid',
        [`& .${chipClasses.icon}, & .${chipClasses.deleteIcon}`]: chipIconStyles('var(--mds-Chip-outlinedIconColor)'),
        [`&:hover .${chipClasses.deleteIcon}`]: chipIconStyles('var(--mds-Chip-outlinedIconColor)'),
        [focusVisibleSelector]: {
          color: 'var(--mds-Chip-outlinedColor)',
          backgroundColor: 'var(--mds-Chip-outlinedBg)',
          borderColor: 'var(--mds-Chip-outlinedBorderColor)',
          ...focusRingStyles(),
        },
        [`&.${chipClasses.disabled}`]: disabledChipStyles({ outlined: true }),
      },
    },
    {
      props: (props) => isOutlineVariant(props.variant) && isClickable(props.clickable),
      style: {
        color: 'var(--mds-Chip-outlinedColor)',
        backgroundColor: 'var(--mds-Chip-outlinedBg)',
        borderColor: 'var(--mds-Chip-outlinedBorderColor)',
        borderWidth: chipTokens.borderWidth,
        borderStyle: 'solid',
        '&:hover': {
          color: 'var(--mds-Chip-outlinedColor)',
          backgroundColor: 'var(--mds-Chip-outlinedHoverBg)',
          backgroundImage: 'none',
          borderColor: 'var(--mds-Chip-outlinedBorderColor)',
        },
        [focusVisibleSelector]: {
          color: 'var(--mds-Chip-outlinedColor)',
          backgroundColor: 'var(--mds-Chip-outlinedBg)',
          borderColor: 'var(--mds-Chip-outlinedBorderColor)',
          ...focusRingStyles(),
        },
        [`& .${chipClasses.icon}, & .${chipClasses.deleteIcon}`]: chipIconStyles('var(--mds-Chip-outlinedIconColor)'),
        [`&:hover .${chipClasses.deleteIcon}`]: chipIconStyles('var(--mds-Chip-outlinedIconColor)'),
        [`&.${chipClasses.disabled}`]: disabledChipStyles({ outlined: true }),
      },
    },
    {
      props: (props) => isOutlineVariant(props.variant) && props.ownerState.color === 'default',
      style: {
        borderColor: 'var(--mds-Chip-outlinedBorderColor)',
        background: 'none',
      },
    },
    {
      props: (props) => props.variant === 'soft' && props.selected === true,
      style: selectedChipStyles('soft'),
    },
    {
      props: (props) => isFilledVariant(props.variant) && props.selected === true,
      style: selectedChipStyles('filled'),
    },
    {
      props: (props) => isOutlineVariant(props.variant) && props.selected === true,
      style: selectedChipStyles('outlined'),
    },
    {
      props: { size: 'large' },
      style: ({ theme }) => {
        const sizeTokens = chipSizeTokens.large;
        return {
          height: sizeTokens.height,
          [`& .${chipClasses.label}`]: {
            ...typographyStyles(theme.typography.label1),
            paddingInline: sizeTokens.paddingX,
          },
          [`& .${chipClasses.avatar}`]: {
            ...avatarStyles(sizeTokens.avatarSize, sizeTokens.avatarPaddingLeft, '0.75rem'),
          },
          [`& .${chipClasses.icon}`]: {
            fontSize: iconSizes.lg,
          },
          [`& .${chipClasses.deleteIcon}`]: {
            fontSize: deleteIconSize(iconSizes.lg),
            marginLeft: 0,
            marginRight: sizeTokens.paddingX,
          },
        };
      },
    },
    {
      props: { size: 'medium' },
      style: ({ theme }) => {
        const sizeTokens = chipSizeTokens.medium;
        return {
          height: sizeTokens.height,
          [`& .${chipClasses.label}`]: {
            ...typographyStyles(theme.typography.label2),
            paddingInline: sizeTokens.paddingX,
          },
          [`& .${chipClasses.avatar}`]: {
            ...avatarStyles(sizeTokens.avatarSize, sizeTokens.avatarPaddingLeft, '0.625rem'),
          },
          [`& .${chipClasses.icon}`]: {
            fontSize: iconSizes.md,
          },
          [`& .${chipClasses.deleteIcon}`]: {
            fontSize: deleteIconSize(iconSizes.md),
            marginLeft: 0,
            marginRight: sizeTokens.paddingX,
          },
        };
      },
    },
    {
      props: { size: 'small' },
      style: ({ theme }) => {
        const sizeTokens = chipSizeTokens.small;
        return {
          height: sizeTokens.height,
          [`& .${chipClasses.label}`]: {
            ...typographyStyles(theme.typography.label3),
            paddingInline: sizeTokens.paddingX,
          },
          [`& .${chipClasses.avatar}`]: {
            ...avatarStyles(sizeTokens.avatarSize, sizeTokens.avatarPaddingLeft, '0.5rem'),
          },
          [`& .${chipClasses.icon}`]: {
            fontSize: iconSizes.sm,
          },
          [`& .${chipClasses.deleteIcon}`]: {
            fontSize: deleteIconSize(iconSizes.sm),
            marginLeft: 0,
            marginRight: sizeTokens.paddingX,
          },
        };
      },
    },
  ],
};
